import React, { memo, useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ContentBox } from "../../libs/theme";
import { getText } from "../../libs/utils";
import API from '../../libs/API';
import TimerTile from './TimerTile';
import snsWebSdk from '@sumsub/websdk';
import * as Sentry from "@sentry/react";

const NvWidgetComponent = ({ schema, clientKey, isOpen, onClose, onSuccess }) => {
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [opened, setOpened] = useState(false);

  const openWidgetHandler = useCallback(() => {
    if (window.KYCWidget && !opened) {
      window.KYCWidget.setupKYC({
        schemaId: schema,
        clientKey: clientKey,
        theme: "light",
        closeCb: () => {
          setOpened(false);
          onClose()
        },
        successCb: (payload) => onSuccess(payload),
      });
      setOpened(true);
    }
  }, [schema, clientKey, onClose, onSuccess]);

  const loadWidgetScript = useCallback(() => {
    if (window.KYCWidget || document.getElementById("kyc-widget-script-id")) {
      setWidgetLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "kyc-widget-script-id";
    script.src = "https://kyc.neuro-vision.ru/lib/widget-lib.js";
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onload = () => setWidgetLoaded(true);
    script.onerror = () => {
      console.error("Failed to load KYC widget script");
      Sentry.captureException("Failed to load KYC widget script");
    };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    loadWidgetScript();
  }, []);

  useEffect(() => {
    if (isOpen && widgetLoaded) {
      openWidgetHandler();
    }
  }, [isOpen, widgetLoaded, openWidgetHandler]);

  return (
    <div />
  );
}

export const NvWidget = memo(NvWidgetComponent);

const SumSubWidgetComponent = ({ accessToken }) => {
  const launchWebSdk = useCallback((token) => {
    const getNewAccessToken = async () => {
      throw new Error("Access token expired. Please reload the session.");
    }

    let snsWebSdkInstance = snsWebSdk
      .init(
        token,
        () => getNewAccessToken()
      )
      .withConf({
        lang: localStorage.lang,
        theme: "light",
        country: "RUS",
      })
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on("idCheck.onStepCompleted", (payload) => {
        console.log("onStepCompleted", payload);
      })
      .on("idCheck.onUploadError", (error) => {
        console.log("onUploadError", error);
        Sentry.captureException("idCheck.onUploadError - SumSub WebSDK error", {
          extra: { error }
        });
      })
      .on("idCheck.onUploadWarning", (warning) => {
        console.log("onUploadWarning", warning);
        Sentry.captureException("idCheck.onUploadWarning - SumSub WebSDK warning", {
          extra: { warning }
        });
      })
      .on("idCheck.onError", (error) => {
        console.log("onError", error);
        Sentry.captureException("idCheck.onError - SumSub WebSDK error", {
          extra: { error }
        });
      })
      .build();

    try {
      snsWebSdkInstance.launch("#sumsub-websdk-container");
    } catch (error) {
      console.error("Error launching SumSub WebSDK:", error);
      Sentry.captureException("Error launching SumSub WebSDK", {
        extra: { error }
      });
    }
  }, []);

  useEffect(() => {
    if (accessToken) {
      launchWebSdk(accessToken);
    }
  }, [accessToken, launchWebSdk]);

  return <Box id="sumsub-websdk-container" sx={{}} />;
}

export const SumSubWidget = memo(SumSubWidgetComponent);

export default function OrderKYC({ order, handleTimeOut }) {
  const [kycData, setKycData] = React.useState(null);

  const createSession = async () => {
    try {
      const response = await API.createKYCSession(order.code);
      if (response.status === 200) {
        setKycData(response.data?.session);
      }
    } catch (error) {
      console.error("Error creating KYC session:", error);
    }
  };

  useEffect(() => {
    createSession();

  }, []);

  const handleWidgetClose = useCallback(() => {
    console.log("KYC widget closed");

  }, []);

  const handleWidgetSuccess = useCallback((payload) => {
    console.log("KYC completed successfully", payload);
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h3">{getText('orderHeaderStateKYC')}</Typography>
        <TimerTile timerEnd={order.timerEnd} handleTimeOut={handleTimeOut} />
      </Box>
      <Typography variant="regularCaption" >{getText('orderKYCInstruction')}</Typography>
      <Box mt={3}>
        {kycData?.iframeUrl && <iframe
          title="KYC Verification"
          src={kycData ? kycData.iframeUrl : ''}
          allow='camera; microphone'
          width="100%"
          height="650px"
          style={{ border: 'none' }}
        >
        </iframe>}
        {kycData?.service?.toLowerCase() == "neurovision" && <NvWidget
          schema={kycData?.data?.schemaId}
          clientKey={kycData?.data?.clientKey}
          isOpen={true}
          onClose={handleWidgetClose}
          onSuccess={handleWidgetSuccess}
        />}
        {kycData?.service?.toLowerCase() == "sumsub" && <SumSubWidget accessToken={kycData?.data?.accessToken} />}
      </Box>
    </>
  );
}
